import { createContext, useState, ReactNode, Dispatch, SetStateAction, useEffect, useContext } from "react";
import { runQuery } from "../services/apiService";
import { adRecord, Features,SiteFeatures,Site, DocLinks, GlobalSettings, proofFiles,Statuses,Multitenancy } from "../services/interfaces";
import { docLinks,siteFeatures, gSettings,statuses,tenants } from './Configuration';
import { UserContext } from "./UserContext";
import { userNotification,SubmissionForm,dashStatus,trafficFilter } from '../services/interfaces';
import { getUserNotifications } from "../services/apiService";
import { pingAuth } from "../services/apiService";
import { Loader } from "@progress/kendo-react-indicators";
import LogRocket from "logrocket";
import { ZenDeskWidget } from "./ZenDeskChat";
import { State } from "@progress/kendo-data-query";

interface Page {
    fields:string;
    fromGetNext:string;
}

interface PageContextType {
    page: Page;
    setPage: Dispatch<SetStateAction<Page>>;
    selectedTab:number;
    setSelectedTab:Dispatch<SetStateAction<number>>;
    showExpand:boolean;
    setShowExpand:Dispatch<SetStateAction<boolean>>;
    selectedAd:adRecord;
    setSelectedAd:Dispatch<SetStateAction<adRecord>>;
    docLinkSet:DocLinks;
    uiFeatures:Features;
    glSettings:GlobalSettings;
    atolStatuses:Statuses;
    setGlSettings:Dispatch<SetStateAction<GlobalSettings>>;
    showNotifications:boolean;
    setShowNotifications:Dispatch<SetStateAction<boolean>>;
    notifications:Array<userNotification>;
    setNotifications:Dispatch<SetStateAction<Array<userNotification>>>;
    proofFiles:proofFiles;
    setProofFiles:Dispatch<SetStateAction<proofFiles>>;
    showMyQueries:boolean;
    setShowMyQueries:Dispatch<SetStateAction<boolean>>;
    setShowLoading:Dispatch<SetStateAction<boolean>>;
    dashStat:dashStatus;
    refreshActiveGrid:boolean;
    setRefreshActiveGrid:Dispatch<SetStateAction<boolean>>;
    trafficFilter:trafficFilter;
    setTrafficFilter:Dispatch<SetStateAction<trafficFilter>>;
    dash1DataState:State;
    setDash1DataState:Dispatch<SetStateAction<State>>;
    dash2DataState:State;
    setDash2DataState:Dispatch<SetStateAction<State>>;
    dash3DataState:State;
    setDash3DataState:Dispatch<SetStateAction<State>>;
    dash4DataState:State;
    setDash4DataState:Dispatch<SetStateAction<State>>;
    trafficDataState:State;
    setTrafficDataState:Dispatch<SetStateAction<State>>;
    timerActive:boolean;
    setTimerActive:Dispatch<SetStateAction<boolean>>;
    expandMax:boolean;
    setExpandMax:Dispatch<SetStateAction<boolean>>;
    logoPath:string;

}

export const PageContext = createContext<PageContextType | null>(null);

interface PageContextProviderProps {
    children: ReactNode;
}

export function PageContextProvider({children}: PageContextProviderProps){
    const [page, setPage] = useState(
        {
            fields:'',
            fromGetNext:''
        }
    );
    const [docLinkSet, setDocLinkSet] = useState<DocLinks>(
        {
            Links: [
              {
                Placeholder: "",
                Prompt: "",
                Url: ""
              }
            ]
        }
    );    
    const [glSettings, setGlSettings]= useState<GlobalSettings>(
        {
            DebugMode:false,
            UseLogRocket:false,
            LogRocketInit:"",
            MaxUploadsize:"",
            UseStaticDynamicExtension:true,
            StaticAdExtension:".INDD",
            DynamicAdExtension:".FLA",
            ShowDeadlineType:true,
            AllowMultiPickup:false,
            MultiPickupLimit:5,
            EnableCampaign:true,
            TimeZone:"US Eastern Standard Time",
            Locale:"EN-US",
            UseDST:false,
            Source:"",
            MarketingPub:"",
            SpecPub:"",
            SpecDueDays:"30",
            RenamePressReady:true,
            UseAzure:true,
            UseEuroDates:false,
            MaterialsInDeadlineTitle:"",
            ProofOutDeadlineTitle:"",
            ProofInDeadlineTitle:"",
            CompleteDeadlineTitle:"",
            SourcingDeadlineTitle:"",
            HiddenPubs:[],
            ViewNotesDefaultUser:"STUDIO",
            ViewNotesLink:"",
            DTOLink:"",
            HelpLink:"https://atolhelp.dpsadtrackercloud.com/",
            WhatsNewLink:"https://atolhelp.dpsadtrackercloud.com/topics/idh-topic20.htm",
            WikiLink:"",
            PrivacyLink:"https://dpsadtracker.com/dps-privacy-policy/",
            CookieLink:"https://dpsadtracker.com/dps-cookie-policy/",
            EnableNewCustomerContact:true,
            UseSiteTickets:true,
            EnableAdRatings:true,
            RatingsMaxValue:"5",
            EnableDTORedirect:"false",
            DefaultPickupWithChange:true,
            EnableDownloadPDF:true,
            EnableUserGroups:true,
            HideMoreFilesToCome:true,
            UseClipperMode:false,
            UseSaveState:false,
            ChatPluginSrc:"",
            HidePickupOnCorrection:true,
            ShowPickupExpiration:"false",
            BypassInstructionsOnPR:true,
            AllowPickupFromAllCustomers:false,
            OverwriteWarningOnPickupNoChange:"false",
            AllowPickupChangeOnDifferentCustomer:false,
            MarketingOrdersRMOnly:"",
            ChangeCorrResolveHoldToSub:false,
            MultiPageProofField:"purge_date",
            UseAPIMail:true,
            SizeField:"size",
            AtcRefreshInterval:60000, //miliseconds
            DisableSourceForSites:false,
            EnableATConfig:true,
            EnableTrustedReps:true,
            AllowPickupComponents:true,
            UseV7Security:true,
            UntrustedSubmissionHeader:"(REVIEW REQUIRED)",
            MMSimpleMode:false,
            AllowAdTrackerLogin:false,
            EnableCorrectionDeadlines:true,
            Features:[]
        }
    );
    const [uiFeatures, setUiFeatures]= useState<Features>(
        {
            Security: "",
            adJacketEnabled:false,
            editTagLine:true,
            adJacketVisible:true,
            newAd:"",
            specAd:"",
            osOrder:"",
            submission:"",
            correction:"",
            annotation:"",
            approval:"",
            dto:"",
            runningChange:"",
            changeRundates:"",
            JDVault:"",
            contacts:"",
            rundates:"",
            viewComponents:"",
            downloadComponents:"",
            jobTicket:"",
            history:"",
            changeStatus:"",
            changeLockStatus:"",
            manualAdNumber:"",
            submissionEmail:"",
            correctionEmail:"",
            approvalEmail:"",
            pressReady:true,
            multiPageSubmission:true,
            manageUsers:true,
            manageOrphans:true,
            assignReps:true,
            userAttributes:true,
            manageGlobalQueries:true,
            UserTemplate:"",
            simplePressReady:true,
            fastAdBuilderRM:true,
            fastAdBuilderPrint:true,
            fastAdBuilderHold:true,
            fastAdBuilderReset:true,
            pickupTo:"",
            downloadAd:true,
            getNext:true,
            adTrafficController:true,
            addRunDates:true,
            enableFABSelfService:true,
            sourceAd:true,
            makeHot:true,
            makeRush:true,
            pickupComponents:true,
            customerContactTracking:true,
            showPressReadyForRM:true,
            showProofNotes:true,
            osDefaultRep:"",
            showPackageManager:true,
            sendReadOnlyProof:false
        }
    );
    const [dashStat, setDashStat] = useState<dashStatus>(
        {Dash1Status:"",
        Dash2Status:"",
        Dash3Status:"",
        Dash4Status:"",
        Dash1Title:"",
        Dash2Title:"",
        Dash3Title:"",
        Dash4Title:"",
        TrafficTitle:""
    });
    

    const [atolStatuses, setATOLStatuses]= useState<Statuses>({
        Approve:"",
        SpecApprove:"",
        RMApprove:"",
        Correction:"",
        CorrectionNoFiles:"",
        CorrectionHold:"",
        Submission:"",
        SubmissionNoFiles:"",
        SubmissionHold:"",
        PRSubmission:"",
        PRSubmissionHold:"",
        NewAdOrder:"",
        NewMarketingOrder:"",
        NewSourcingOrder:"",
        NewSpecOrder:"",
        NewDTO:"",
        HoldStatuses:"",
        ProofStatuses:"",
        LockStatuses:"",
        LockSetStatusOn:"",
        DTOStatuses:"",
        SendToPost:"",
        CorrectionStatuses:"",
        PickupFilter:"",
        AdCompleteStatuses:"",
        ClipperAssociatedAdStatus:"",
        FABLaunchStatus:"",
        FABReturnStatusRM:"",
        FABReturnHoldStatusRM:"",
        FABReturnStatusPrint:"",
        FABReturnHoldStatusPrint:"",
        SourceOnStatus:"",
        RushLockStatuses:"",
        ProofNotesStatuses:"",
        HoldNotesStatuses:"",
        UntrustedSubmission:"",
        UntrustedCorrection:"",
        SendProof:"",
        MatRequest:"",
        FrequenceSetStatus:"",
        CreativeRequestedCheckStatus:"",
        CreativeRequestedSetStatus:"",
        FileSyncCRRMStatus:"",
        FileSyncCRStatus:"",
        FileSyncCRRMHoldStatus:"",
        FileSyncCRHoldStatus:"",
        FileSyncCorrStatus:"",
        FileSyncCorrHoldStatus:"",
        FileSyncNewAdStatus:"",
        FileSyncPreStatus:"",
        FileSyncNewAdHoldStatus:"",
        FileSyncIncompleteStatus:""
    })

    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [showExpand, setShowExpand]=useState(false);
    const [showMyQueries, setShowMyQueries]=useState(false);
    const [expandMax, setExpandMax]=useState(false);
    
    const [selectedAd, setSelectedAd] = useState<adRecord>(
        {
            accountNotes:0,
            category:"",
            comment:"",
            comment2:"",
            completedDeadline:0,
            cropping1:"",
            currentDeadline:0,
            customer:"",
            element:"",
            elementType:"",
            epsToPagination:0,
            expr1:0,
            file1:"",
            fileSize1:"",
            fileSize2:"",
            firstRun:"",
            groupAdNum:"",
            isCrossSold:"",
            materialDeadline:0,
            materialReceived:0,
            owner:"",
            path:"",
            proofInDeadline:0,
            proofOutDeadline:0,
            proofSent:0,
            pub0:"",
            pubDate:0,
            publishDate:"",
            purgeDate:"",
            queue:"",
            rCopyLines:"",
            rCopyStatus:"",
            salesman:"",
            section:"",
            size:"",
            slug:"",
            status:"",
            temp1:"",
            temp3:"",
            temp4:"",
            temp5:"",
            temp6:"",
            tmpChar1:"",
            tmpChar2:"",
            tmpChar3:"",
            tmpInt2:0,
            watching:"",
            assignedBy:"",
            assignedFullName:"",
            assignedTo:"",
            ccSubStatus:"",
            trafficStatus:"",
            mmJobName:""
        }
    );
    const [proofFiles, setProofFiles] = useState<proofFiles>(
        {
            thumbURL:"",
            pdfURL:"",
            ticketURL:"",
            rmProof:""
        }
    )
    const [timerActive, setTimerActive]=useState(false);

    const [showNotifications, setShowNotifications]=useState(false);
    const [notifications, setNotifications]=useState<Array<userNotification>>(
            [{
                element:"",
                username:"",
                succeff_fail:"",
                date_time:new Date(),
                status:"",
                origin:"",
                state:"",
                description:"",
                type:"",
            }]
        );
    const [trafficFilter, setTrafficFilter]=useState<trafficFilter>({
        group:{groupId:"",groupName:"",primary:false},
        filter:{name: "Show All", value: 0},
        pagDate:null,
        pagEndDate:null
    })
    const [showLoading, setShowLoading]=useState(false);
    const [refreshActiveGrid, setRefreshActiveGrid] = useState(false);
    const audio = new Audio("/assets/ding-101492.mp3");

    const initialDataState: State = {
        sort: [{ field: 'pubDate', dir: 'asc' }],
    };

    const [dash1DataState, setDash1DataState]=useState<State>(initialDataState);
    const [dash2DataState, setDash2DataState]=useState<State>(initialDataState);
    const [dash3DataState, setDash3DataState]=useState<State>(initialDataState);
    const [dash4DataState, setDash4DataState]=useState<State>(initialDataState);
    const [trafficDataState, setTrafficDataState]=useState<State>(initialDataState);
    const [notifyInteval,setNotifyInterval]=useState<NodeJS.Timeout | null>(null);
    const [logoPath, setLogoPath]=useState("");

    const getNotifications=()=>{
        console.log("Get notifications");
        getUserNotifications(userContext?.user.username).then(data => {
            setNotifications(data);
            var isNew=false;
            data.map((item:userNotification)=>{
                if(Number(item.state) === 0){
                    isNew=true;
                }
            });
            if (isNew){
                //audio.play();
            }
            
        });   
    }

    const getTheme= async()=>{
        const host = window.location.hostname;
        const themes = await tenants();
        const theme = themes.Tenants.find(tenant=>tenant.Hostnames.includes(host));
        if (theme){
            if (theme.Theme){
                const themePath = "/themes/"+theme.Theme+"/images/Logo.png";
                console.log(themePath);
                return themePath;
            }
        }
        return "/img/designIQ_logo_OneClr_RGB_blue_padding.png";
    }

    const getFeatures=()=>{
        console.log(userContext?.user);
        if (userContext?.user.siteCode){
            siteFeatures(userContext?.user.siteCode).then(site => {
                console.log(site);
                if (site && site.features.length > 0) {
                    const secFeatures = site.features.find(feature=>feature.Security==userContext.user.type.toString());
                    if (secFeatures){
                        setUiFeatures(secFeatures);
                    }
                }else{
                    //User site code not found.
                    const secFeatures = glSettings?.Features.find(feature=>feature.Security==userContext?.user.type.toString())
                    console.log("User Type: " + userContext?.user.type);
                    console.log(secFeatures);
                    if (secFeatures){
                        setUiFeatures(secFeatures);
                    }
                }
            });            
        }else{
            console.log("Site code is blank");
            const secFeatures = glSettings?.Features.find(feature=>feature.Security==userContext?.user.type.toString())
            console.log("User Type: " + userContext?.user.type);
            console.log(secFeatures);
            if (secFeatures){
                setUiFeatures(secFeatures);
            }
        }
    }

    const userContext = useContext(UserContext);


    async function dashData(){
        const user = userContext?.user;    
        const userDir = "users/" + user?.username;
        const dashStatPath = userDir + "/dashStatus.json";
        await fetch(dashStatPath)
            .then((response) => response.json())
            .then((data) => setDashStat(data));
    };

    useEffect(() => {
        gSettings().then(settings => {
            setGlSettings(settings)
        });
        docLinks().then(links => setDocLinkSet(links));
        statuses().then(stats=>setATOLStatuses(stats));
        getTheme().then(path=>setLogoPath(path));
        console.log("User Site Code: " + userContext?.user.siteCode);    
    }, []);  

    useEffect(() => {
        if (userContext?.user.username!=""){
            gSettings().then(settings => {
                setGlSettings(settings)
            });
            dashData();
            setTimerActive(true);
        }
    }, [userContext?.user]);  

    useEffect(()=>{
        getFeatures();
        //Init LogRocket
        if (glSettings.UseLogRocket===true){
            if (userContext?.user){
                LogRocket.init(glSettings.LogRocketInit);
                LogRocket.identify(userContext.user.username);
            } 
        }
    },[glSettings]);
    return (
        <PageContext.Provider value={{page,setPage,selectedTab,setSelectedTab,showExpand,setShowExpand, selectedAd,setSelectedAd,docLinkSet, uiFeatures, glSettings, atolStatuses, setGlSettings, showNotifications, setShowNotifications,notifications,setNotifications, proofFiles, setProofFiles, showMyQueries, setShowMyQueries, setShowLoading, dashStat, refreshActiveGrid,setRefreshActiveGrid,trafficFilter,setTrafficFilter, dash1DataState,setDash1DataState,dash2DataState,setDash2DataState,dash3DataState,setDash3DataState,dash4DataState,setDash4DataState,trafficDataState,setTrafficDataState,timerActive,setTimerActive, expandMax, setExpandMax,logoPath}}>
            {showLoading && (
                <div className="loader-overlay">
                    <Loader themeColor="primary" type="converging-spinner" size="large"/>
                </div>
            )}
            {children}
            <ZenDeskWidget></ZenDeskWidget>
        </PageContext.Provider>
    );
}